import useStringParam from "@tiny/components/src/hooks/useStringParam";
import React from "react";
import { useTinyHost } from "@tiny/host-contract";
import { SegmentIdentityView1 } from "@tiny/api";
import { useFeatureAvailability } from "../../hooks/useFeatureAvailability";
import { usePostCustomerStatisticsTotalQuery } from "@tiny/api";

export function useAudienceHome() {
  const [canAccessCustomSegmentsTab, requestUpgradeForCustomSegments] =
    useFeatureAvailability("CUSTOM_SEGMENTS");
  const [canAccessPredefinedSegmentsTab, requestUpgradeForPredefinedSegments] =
    useFeatureAvailability("PRE_BUILD_CUSTOM_SEGMENTS");
  const [canAccessAITrafficControlTab, requestUpgradeForAITrafficControl] =
    useFeatureAvailability("AI_TRAFFIC_CONTROL");

  const { navigate } = useTinyHost();

  const [activeTab, setActiveTab] = useStringParam("tab", "sources");

  const [addAudienceDialogOpen, setAddAudienceDialogOpen] =
    React.useState(false);

  const openAddAudienceDialog = React.useCallback(() => {
    setAddAudienceDialogOpen(true);
  }, []);

  const closeAddAudienceDialog = React.useCallback(() => {
    setAddAudienceDialogOpen(false);
  }, []);

  const gotoAudienceDetails = React.useCallback(
    async (segment: SegmentIdentityView1) => {
      refetch();
      navigate.to("/main/audience");
    },
    [navigate]
  );

  const {
    data: audienceUsage,
    isLoading: isAudienceLoading,
    refetch,
  } = usePostCustomerStatisticsTotalQuery({});

  const isEmpty =
    audienceUsage?.numberOfUniqueCustomers === 0 && !isAudienceLoading;

  // TODO extract in separate hook for handling tab permission checking
  const onChangeTab = React.useCallback(
    (tab: string) => {
      if (tab === "custom_segments" && canAccessCustomSegmentsTab === false) {
        requestUpgradeForCustomSegments();
        return;
      }
      if (
        tab === "predefined_segments" &&
        canAccessPredefinedSegmentsTab === false
      ) {
        requestUpgradeForPredefinedSegments();
        return;
      }
      if (
        tab === "ai_traffic_control" &&
        canAccessAITrafficControlTab === false
      ) {
        requestUpgradeForAITrafficControl();
        return;
      }
      setActiveTab(tab);
    },
    [
      canAccessCustomSegmentsTab,
      requestUpgradeForCustomSegments,
      canAccessPredefinedSegmentsTab,
      requestUpgradeForPredefinedSegments,
      canAccessAITrafficControlTab,
      requestUpgradeForAITrafficControl,
      setActiveTab,
    ]
  );

  React.useLayoutEffect(() => {
    if (
      !isAudienceLoading &&
      ((activeTab === "custom_segments" &&
        canAccessCustomSegmentsTab === false) ||
        (activeTab === "predefined_segments" &&
          canAccessPredefinedSegmentsTab === false) ||
        (activeTab === "ai_traffic_control" &&
          canAccessAITrafficControlTab === false))
    ) {
      setActiveTab("sources");
    }
  }, [
    activeTab,
    canAccessCustomSegmentsTab,
    canAccessPredefinedSegmentsTab,
    canAccessAITrafficControlTab,
    isAudienceLoading,
    setActiveTab,
  ]);

  return {
    activeTab,
    addAudienceDialogOpen,
    openAddAudienceDialog,
    closeAddAudienceDialog,
    gotoAudienceDetails,
    isEmpty,
    isAudienceLoading,
    onChangeTab,
    canAccessPredefinedSegmentsTab,
  };
}
